import { useEffect, useState } from "react";
import { Globe, Trash2, Star, Plus, CheckCircle, AlertCircle, Palette } from "lucide-react";
import { theme, card, btnPrimary, inputStyle } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { ABACAS_PRINT } from "../lib/exportBrand";
import { StatusTag } from "../components/Drawing";

// An unset colour keeps the platform's own brand (backend/src/lib/brand.ts, mirrored in
// lib/exportBrand.ts); the picker shows that colour rather than an arbitrary one.
const PLATFORM_DEFAULT_COLOR = `#${ABACAS_PRINT.accent}`;

// ── Types ────────────────────────────────────────────────────────────────────

interface Domain {
  id: string;
  hostname: string;
  isPrimary: boolean;
  createdAt: string;
  verified: boolean;
  dnsRecord: { type: string; name: string; value: string } | null;
}

interface EntraConfig {
  ownEntraClientId: string | null;
  ownEntraTenantId: string | null;
}

/** GET /branding: the firm's identity (columns on the Consultancy row). */
interface BrandingData {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  contactEmail: string | null;
  website: string | null;
}

// ── Shared styles ─────────────────────────────────────────────────────────────

const label: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: theme.color.subtle,
  display: "block", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em",
};

const stepNum: React.CSSProperties = {
  width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
  background: theme.color.accentBg, border: `1px solid ${theme.color.accent}`,
  display: "flex", alignItems: "center", justifyContent: "center",
  fontSize: 11, fontWeight: 700, color: theme.color.accent,
};

// ── Main component (/consultant/settings/branding) ────────────────────────────
// One page: the firm's identity (name, logo, colours, contact) above the
// white-label custom domains and the firm's own Entra app registration.

export function ConsultantBrandingPage() {
  const [domains,      setDomains]      = useState<Domain[]>([]);
  const [entra,        setEntra]        = useState<EntraConfig>({ ownEntraClientId: null, ownEntraTenantId: null });
  const [loading,      setLoading]      = useState(true);
  const [loadError,    setLoadError]    = useState("");

  useEffect(() => {
    Promise.all([
      api.get<Domain[]>("/branding/domains"),
      api.get<EntraConfig>("/branding/entra"),
    ])
      .then(([d, e]) => { setDomains(d); setEntra(e); })
      .catch(() => setLoadError("Failed to load white-label configuration"))
      .finally(() => setLoading(false));
  }, []);

  if (loading)   return <LoadingBlock />;
  if (loadError) return <div style={{ padding: 24, color: theme.color.dangerText, fontSize: 13 }}>{loadError}</div>;

  return (
    <div className="page-pad">
      <h1 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>
        Branding &amp; White Label
      </h1>
      <p style={{ margin: "0 0 28px", fontSize: 13, color: theme.color.subtle, lineHeight: 1.6 }}>
        Your firm's name, logo and colours appear throughout the portal your clients use. Serve the platform
        from your own domain to keep Abacas invisible: each domain needs a DNS CNAME, an Azure custom domain
        entry and your own Microsoft Entra app registration.
      </p>

      <FirmIdentitySection />
      <DomainsSection domains={domains} setDomains={setDomains} />
      <EntraSection entra={entra} setEntra={setEntra} />
      <SetupGuide />
    </div>
  );
}

// ── Firm identity section ─────────────────────────────────────────────────────

function FirmIdentitySection() {
  const [form,    setForm]    = useState<Partial<BrandingData>>({});
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState("");

  useEffect(() => {
    api.get<BrandingData>("/branding")
      .then((d) => setForm(d))
      .catch(() => setError("Could not load branding settings"))
      .finally(() => setLoading(false));
  }, []);

  function set(key: keyof BrandingData, value: string) {
    setSaved(false);
    setForm((prev) => ({ ...prev, [key]: value || null }));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      await api.put("/branding", {
        name:         form.name,
        logoUrl:      form.logoUrl || null,
        faviconUrl:   form.faviconUrl || null,
        primaryColor: form.primaryColor || null,
        accentColor:  form.accentColor || null,
        contactEmail: form.contactEmail || null,
        website:      form.website || null,
      });
      setSaved(true);
    } catch (err: unknown) {
      const ex = err as { payload?: { error?: string }; message?: string };
      setError(apiErrorMessage(ex, ex?.message || "Save failed"));
    } finally {
      setSaving(false);
    }
  }

  const colorField = (title: string, key: "primaryColor" | "accentColor") => (
    <div>
      <label style={label}>{title}</label>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <input
          type="color"
          aria-label={`${title} picker`}
          value={form[key] || PLATFORM_DEFAULT_COLOR}
          onChange={(e) => set(key, e.target.value)}
          style={{ width: 40, height: 34, padding: 2, border: `1px solid ${theme.color.border}`, borderRadius: 4, cursor: "pointer", background: theme.color.bg }}
        />
        <input
          style={{ ...inputStyle, flex: 1, fontFamily: "monospace" }}
          value={form[key] ?? ""}
          onChange={(e) => set(key, e.target.value)}
          placeholder={`Platform default (${PLATFORM_DEFAULT_COLOR})`}
          pattern="^#[0-9a-fA-F]{6}$"
        />
        {form[key] && (
          <button type="button" onClick={() => set(key, "")} style={{ background: "transparent", border: `1px solid ${theme.color.border}`, color: theme.color.muted, borderRadius: 4, padding: "6px 10px", fontSize: 12, cursor: "pointer" }}>
            Use platform default
          </button>
        )}
      </div>
    </div>
  );

  return (
    <form onSubmit={handleSave} style={{ ...card, marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <Palette size={16} color={theme.color.accent} />
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading }}>Firm identity</div>
      </div>
      <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 18, lineHeight: 1.6 }}>
        Shown in the sidebar, invite emails and the client portal. Colours must be six-digit hex values.
      </div>

      {loading ? (
        <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={label}>Firm name</label>
            <input style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} value={form.name ?? ""} onChange={(e) => set("name", e.target.value)} required />
          </div>
          <div>
            <label style={label}>Logo URL</label>
            <input style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} type="url" value={form.logoUrl ?? ""} onChange={(e) => set("logoUrl", e.target.value)} placeholder="https://" />
          </div>
          <div>
            <label style={label}>Favicon URL</label>
            <input style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} type="url" value={form.faviconUrl ?? ""} onChange={(e) => set("faviconUrl", e.target.value)} placeholder="https://" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
            {colorField("Primary colour", "primaryColor")}
            {colorField("Accent colour", "accentColor")}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
            <div>
              <label style={label}>Contact email</label>
              <input style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} type="email" value={form.contactEmail ?? ""} onChange={(e) => set("contactEmail", e.target.value)} />
            </div>
            <div>
              <label style={label}>Website</label>
              <input style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }} type="url" value={form.website ?? ""} onChange={(e) => set("website", e.target.value)} placeholder="https://" />
            </div>
          </div>

          {form.slug && (
            <div style={{ fontSize: 11, color: theme.color.subtle, padding: "8px 0 0", borderTop: `1px solid ${theme.color.border}` }}>
              Your slug is <strong>{form.slug}</strong>, used in invite links and white-label URLs.
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
              {saving ? "Saving" : "Save identity"}
            </button>
            {saved && <span style={{ fontSize: 12, color: theme.color.successText, display: "flex", alignItems: "center", gap: 5 }}><CheckCircle size={13} /> Saved</span>}
            {error && <span style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</span>}
          </div>
        </div>
      )}
    </form>
  );
}

// ── Domains section ───────────────────────────────────────────────────────────

function DomainsSection({ domains, setDomains }: {
  domains: Domain[];
  setDomains: React.Dispatch<React.SetStateAction<Domain[]>>;
}) {
  const [hostname,   setHostname]   = useState("");
  const [isPrimary,  setIsPrimary]  = useState(false);
  const [adding,     setAdding]     = useState(false);
  const [addError,   setAddError]   = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [verifyingId, setVerifyingId] = useState<string | null>(null);
  const [verifyError, setVerifyError] = useState<Record<string, string>>({});
  const [primaryId,   setPrimaryId]   = useState<string | null>(null);
  const [primaryError, setPrimaryError] = useState("");

  async function handleMakePrimary(id: string) {
    setPrimaryId(id); setPrimaryError("");
    try {
      setDomains(await api.post<Domain[]>(`/branding/domains/${id}/primary`));
    } catch (e: unknown) {
      setPrimaryError(e instanceof Error ? e.message : "Could not change the primary domain");
    } finally {
      setPrimaryId(null);
    }
  }

  async function handleVerify(id: string) {
    setVerifyingId(id); setVerifyError(prev => ({ ...prev, [id]: "" }));
    try {
      const updated = await api.post<Domain>(`/branding/domains/${id}/verify`);
      setDomains(prev => prev.map(d => (d.id === id ? updated : d)));
    } catch (e: unknown) {
      const err = e as { payload?: { message?: string; error?: string }; message?: string };
      setVerifyError(prev => ({ ...prev, [id]: apiErrorMessage(err, err?.message || "Verification failed") }));
    } finally {
      setVerifyingId(null);
    }
  }

  async function handleAdd() {
    const h = hostname.trim().toLowerCase();
    if (!h) return;
    setAdding(true); setAddError("");
    try {
      const domain = await api.post<Domain>("/branding/domains", { hostname: h, isPrimary });
      setDomains(prev => {
        const list = isPrimary ? prev.map(d => ({ ...d, isPrimary: false })) : prev;
        return [...list, domain];
      });
      setHostname(""); setIsPrimary(false);
    } catch (e: unknown) {
      const err = e as { payload?: { error?: string }; message?: string };
      setAddError(apiErrorMessage(err, err?.message || "Failed to add domain"));
    } finally {
      setAdding(false);
    }
  }

  async function handleDelete(id: string) {
    setDeletingId(id);
    try {
      await api.del(`/branding/domains/${id}`);
      setDomains(prev => prev.filter(d => d.id !== id));
    } catch {
      // leave the row — a transient failure shouldn't vanish it
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div style={{ ...card, marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <Globe size={16} color={theme.color.accent} />
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading }}>Custom Domains</div>
      </div>
      <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 18, lineHeight: 1.6 }}>
        Hostnames registered here become valid origins for the platform once you have proved you control
        them: add the TXT record shown for each domain at your DNS provider, then click Verify. The DNS CNAME
        and Azure custom domain must also be configured — see the setup guide below.
      </div>

      {/* Domain list */}
      {domains.length > 0 && (
        <div style={{ marginBottom: 16, display: "flex", flexDirection: "column", gap: 8 }}>
          {domains.map(d => (
            <div
              key={d.id}
              style={{
                display: "flex", alignItems: "center", gap: 10, padding: "10px 14px",
                borderRadius: 3, background: theme.color.panelAlt,
                border: `1px solid ${d.isPrimary ? theme.color.accent : theme.color.border}`,
              }}
            >
              <code style={{ flex: 1, fontSize: 13, color: theme.color.text, fontFamily: "monospace" }}>
                {d.hostname}
              </code>
              {d.isPrimary && (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  fontSize: 10, fontWeight: 700, color: theme.color.accent,
                  background: theme.color.accentBg, borderRadius: 3, padding: "2px 8px",
                  textTransform: "uppercase", letterSpacing: "0.05em",
                }}>
                  <Star size={10} /> Primary
                </span>
              )}
              {!d.isPrimary && d.verified && (
                <button
                  onClick={() => handleMakePrimary(d.id)}
                  disabled={primaryId === d.id}
                  title="Send your clients to this hostname"
                  style={{ background: "none", border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "3px 8px", fontSize: 11, color: theme.color.muted, cursor: "pointer", opacity: primaryId === d.id ? 0.6 : 1 }}
                >
                  {primaryId === d.id ? "Saving…" : "Make primary"}
                </button>
              )}
              {d.verified ? (
                <StatusTag tone="met">Verified</StatusTag>
              ) : (
                <button
                  onClick={() => handleVerify(d.id)}
                  disabled={verifyingId === d.id}
                  style={{ ...btnPrimary, padding: "4px 10px", fontSize: 11, opacity: verifyingId === d.id ? 0.6 : 1 }}
                >
                  {verifyingId === d.id ? "Checking…" : "Verify"}
                </button>
              )}
              <button
                onClick={() => handleDelete(d.id)}
                disabled={deletingId === d.id}
                title="Remove domain"
                style={{
                  background: "none", border: "none", cursor: "pointer", padding: 4, borderRadius: 3,
                  color: theme.color.subtle, opacity: deletingId === d.id ? 0.5 : 1,
                  display: "flex", alignItems: "center",
                }}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {primaryError && <div role="alert" style={{ fontSize: 12, color: theme.color.dangerText, marginBottom: 12 }}>{primaryError}</div>}

      {/* Pending DNS records */}
      {domains.filter(d => !d.verified && d.dnsRecord).map(d => (
        <div key={`dns-${d.id}`} style={{ marginBottom: 12, padding: "10px 14px", borderRadius: 3, background: theme.color.panelAlt, border: `1px dashed ${theme.color.border}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.subtle, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            Add this DNS record to verify {d.hostname}
          </div>
          <div style={{ fontSize: 12, color: theme.color.text, fontFamily: "monospace", lineHeight: 1.8, wordBreak: "break-all" }}>
            <div>Type: {d.dnsRecord!.type}</div>
            <div>Name: {d.dnsRecord!.name}</div>
            <div>Value: {d.dnsRecord!.value}</div>
          </div>
          {verifyError[d.id] && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 12, color: theme.color.dangerText }}>
              <AlertCircle size={13} /> {verifyError[d.id]}
            </div>
          )}
        </div>
      ))}

      {/* Add domain form */}
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <input
            value={hostname}
            onChange={e => setHostname(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleAdd()}
            placeholder="portal.yourfirm.com"
            style={{ ...inputStyle, width: "100%", boxSizing: "border-box", fontFamily: "monospace", fontSize: 13 }}
          />
        </div>
        <button
          onClick={handleAdd}
          disabled={adding || !hostname.trim()}
          style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6, opacity: (adding || !hostname.trim()) ? 0.6 : 1, whiteSpace: "nowrap" }}
        >
          <Plus size={14} /> {adding ? "Adding…" : "Add domain"}
        </button>
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, cursor: "pointer", width: "fit-content" }}>
        <input type="checkbox" checked={isPrimary} onChange={e => setIsPrimary(e.target.checked)} />
        <span style={{ fontSize: 12, color: theme.color.subtle }}>Set as primary domain</span>
      </label>

      {addError && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, fontSize: 12, color: theme.color.dangerText }}>
          <AlertCircle size={13} /> {addError}
        </div>
      )}
    </div>
  );
}

// ── Entra section ─────────────────────────────────────────────────────────────

function EntraSection({ entra, setEntra }: {
  entra: EntraConfig;
  setEntra: React.Dispatch<React.SetStateAction<EntraConfig>>;
}) {
  const [clientId, setClientId] = useState(entra.ownEntraClientId ?? "");
  const [tenantId, setTenantId] = useState(entra.ownEntraTenantId ?? "");
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [error,    setError]    = useState("");
  // The client ID becomes a trusted token audience on the API, so only Abacas
  // (platform owner) may set it — the server rejects everyone else.
  const { user } = useCurrentUser();
  const canEdit = !!user?.isPlatformOwner;

  const isConfigured = !!entra.ownEntraClientId;

  async function handleSave() {
    setSaving(true); setSaved(false); setError("");
    try {
      const updated = await api.put<EntraConfig>("/branding/entra", {
        ownEntraClientId: clientId.trim() || null,
        ownEntraTenantId: tenantId.trim() || null,
      });
      setEntra(updated);
      setSaved(true);
    } catch (e: unknown) {
      const err = e as { payload?: { error?: string }; message?: string };
      setError(apiErrorMessage(err, err?.message || "Save failed"));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ ...card, marginBottom: 20 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: theme.color.heading }}>Entra App Registration</div>
        {isConfigured && (
          <StatusTag tone="met">Configured</StatusTag>
        )}
      </div>
      <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 18, lineHeight: 1.6 }}>
        Users signing in on your custom domain authenticate against your own Microsoft Entra app registration.
        Tokens issued by your app are verified server-side using Microsoft's public keys — your client secret
        never leaves your Azure tenant.
        {!canEdit && (
          <> These values are applied by Abacas during white-label onboarding after your app registration has been
          checked; contact <a href="mailto:sales@abacassecurity.com" style={{ color: theme.color.accentText }}>sales@abacassecurity.com</a> to set or change them.</>
        )}
      </div>

      {canEdit && (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <label style={label}>Application (Client) ID</label>
          <input
            value={clientId}
            onChange={e => setClientId(e.target.value)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            style={{ ...inputStyle, width: "100%", boxSizing: "border-box", fontFamily: "monospace", fontSize: 12 }}
          />
          <div style={{ fontSize: 11, color: theme.color.muted, marginTop: 4 }}>
            Found in Azure Portal → App registrations → your app → Overview
          </div>
        </div>

        <div>
          <label style={label}>Directory (Tenant) ID</label>
          <input
            value={tenantId}
            onChange={e => setTenantId(e.target.value)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            style={{ ...inputStyle, width: "100%", boxSizing: "border-box", fontFamily: "monospace", fontSize: 12 }}
          />
          <div style={{ fontSize: 11, color: theme.color.muted, marginTop: 4 }}>
            Required when a client ID is set: tokens are only accepted from this tenant.
          </div>
        </div>
      </div>
      )}

      {canEdit && (
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 18 }}>
        <button onClick={handleSave} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
          {saving ? "Saving…" : "Save"}
        </button>
        {saved && <span style={{ fontSize: 12, color: theme.color.successText, display: "flex", alignItems: "center", gap: 5 }}><CheckCircle size={13} /> Saved</span>}
        {error && <span style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</span>}
      </div>
      )}

      {isConfigured && (
        <div style={{ marginTop: 16, padding: "10px 14px", borderRadius: 3, background: theme.color.panelAlt, border: `1px solid ${theme.color.border}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: theme.color.subtle, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>Current config</div>
          <div style={{ fontSize: 12, color: theme.color.text, lineHeight: 2 }}>
            <div><span style={{ color: theme.color.subtle }}>Client ID: </span><code style={{ fontFamily: "monospace" }}>{entra.ownEntraClientId}</code></div>
            <div><span style={{ color: theme.color.subtle }}>Tenant: </span><code style={{ fontFamily: "monospace" }}>{entra.ownEntraTenantId ?? "common (multi-tenant)"}</code></div>
            <div><span style={{ color: theme.color.subtle }}>Authority: </span>
              <code style={{ fontFamily: "monospace", fontSize: 11 }}>
                https://login.microsoftonline.com/{entra.ownEntraTenantId ?? "common"}
              </code>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Setup guide ───────────────────────────────────────────────────────────────

function SetupGuide() {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ ...card, borderLeft: `3px solid ${theme.color.accent}` }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          background: "none", border: "none", cursor: "pointer", padding: 0,
          display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%",
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, color: theme.color.heading }}>Setup guide</div>
        <span style={{ fontSize: 12, color: theme.color.accent }}>{open ? "Hide" : "Show"}</span>
      </button>

      {open && (
        <div style={{ marginTop: 16 }}>
          <StepGroup title="1 · Register your Entra app">
            <Step n={1} text="In Azure Portal, go to Microsoft Entra ID → App registrations → New registration." />
            <Step n={2} text={'Name it anything (e.g. "Acme Compliance Portal"). Supported account types: pick based on whether you want single or multi-tenant logins.'} />
            <Step n={3} text="Under Redirect URI, choose Single-page application and enter your full portal domain, e.g. https://portal.acmecyber.com" />
            <Step n={4} text="In Expose an API, set the Application ID URI to api://{your-client-id} and add a scope named access_as_user." />
            <Step n={5} text="Copy the Application (Client) ID and Directory (Tenant) ID from the Overview page and paste them into the Entra App Registration section above." />
          </StepGroup>

          <StepGroup title="2 · Configure DNS">
            <Step n={1} text="In your DNS provider, add a CNAME record for your subdomain (e.g. portal) pointing to the platform's Azure Static Web App hostname." />
            <Step n={2} text="The platform's Static Web App hostname is shown in Azure Portal under the Static Web App resource → Overview → URL. Use just the hostname (no https://)." />
            <Step n={3} text="Wait for DNS propagation (typically a few minutes, up to 24h)." />
          </StepGroup>

          <StepGroup title="3 · Add the custom domain in Azure">
            <Step n={1} text="In Azure Portal, open the Static Web App resource." />
            <Step n={2} text="Go to Custom domains → Add. Enter your full hostname (e.g. portal.acmecyber.com)." />
            <Step n={3} text="Azure will validate the CNAME and provision a free managed TLS certificate automatically." />
            <Step n={4} text="Once validated (usually 1–5 minutes), add the same hostname in the Domains section above." />
          </StepGroup>

          <StepGroup title="4 · Add your redirect URI to the platform app">
            <Step n={1} text="In Azure Portal, find the platform's Entra app registration (ask Abacas support for the client ID if you don't have it)." />
            <Step n={2} text="Under Authentication → Single-page application, add your domain as a redirect URI: https://portal.acmecyber.com" />
            <Step n={3} text="This step is only needed for the platform's own app — your custom Entra app already has the URI from step 1." />
          </StepGroup>

          <div style={{ marginTop: 14, padding: "10px 14px", borderRadius: 3, background: theme.color.panelAlt, fontSize: 12, color: theme.color.subtle, lineHeight: 1.7 }}>
            <strong style={{ color: theme.color.heading }}>How it works: </strong>
            When a user visits your custom domain, the platform fetches your branding (logo, colours) and
            initialises Microsoft login against your Entra app. Tokens carry your client ID as the audience.
            The platform's backend verifies these tokens cryptographically using Microsoft's public keys — your
            client secret stays entirely in your Azure tenant. Abacas never stores or accesses it.
          </div>
        </div>
      )}
    </div>
  );
}

function StepGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.heading, marginBottom: 10 }}>{title}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{children}</div>
    </div>
  );
}

function Step({ n, text }: { n: number; text: string }) {
  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
      <div style={stepNum}>{n}</div>
      <div style={{ fontSize: 12, color: theme.color.subtle, lineHeight: 1.6, paddingTop: 3 }}>{text}</div>
    </div>
  );
}
